// src/pages/LatestRecipesPage.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from 'react-bootstrap';
import Select from 'react-select';
import { Card, CardBody } from '../../components/card/card.jsx';
import glassshimmer from './../../assets/images/glassshimmer.png';
import { fetchLatestRecipesPG } from '../../actions/recipes_actions.js';

// Reusable crafted-cocktails card (already has its own apply/freeze logic)
import CraftedCocktailsExpandCard from '../../components/charts/CraftedCocktailsExpandCard.jsx';

const typeOptions = [
  { value: 'all',     label: 'All' },
  { value: 'curated', label: 'Curated' },
  { value: 'custom',  label: 'Custom' },
  { value: 'unknown', label: 'Unknown' },
];

const LatestRecipesPG = () => {
  const dispatch = useDispatch();
  const themeColor = getComputedStyle(document.body).getPropertyValue('--bs-theme').trim();

  // Store data/loading from Redux
  const {
    latest_pgrecipes_info,
    loading_latest_pgrecipes,
    latest_pgrecipes_error,
  } = useSelector(state => state.recipes);

  // Applied (committed) filters for Latest Recipes list
  const [appliedType,  setAppliedType]  = useState(typeOptions[0]);
  const [appliedLimit, setAppliedLimit] = useState(50);

  // Local (editable) filters
  const [localType,  setLocalType]  = useState(appliedType);
  const [localLimit, setLocalLimit] = useState(appliedLimit);

  // Freeze list & show overlay spinner while applying/loading
  const [isApplying, setIsApplying] = useState(false);
  const pendingApplyRef = useRef(null);

  // Initial fetch on mount (once)
  useEffect(() => {
    dispatch(fetchLatestRecipesPG(appliedType.value, String(appliedLimit)));
  }, [dispatch]); // applied defaults are set in useState above

  // After spinner paints, kick off fetch (only when there are changes)
  useEffect(() => {
    if (!isApplying || !pendingApplyRef.current) return;

    const { nextType, nextLimit } = pendingApplyRef.current;

    // Mark as applied up-front so repeated clicks with same values do nothing
    setAppliedType(nextType);
    setAppliedLimit(nextLimit);

    dispatch(fetchLatestRecipesPG(nextType.value, String(nextLimit)));
  }, [isApplying, dispatch]);

  // Turn off applying once Redux loading finishes
  const prevLoadingRef = useRef(loading_latest_pgrecipes);
  useEffect(() => {
    if (prevLoadingRef.current && !loading_latest_pgrecipes) {
      setIsApplying(false);
      pendingApplyRef.current = null;
    }
    prevLoadingRef.current = loading_latest_pgrecipes;
  }, [loading_latest_pgrecipes]);

  // Apply button handler — do nothing if unchanged
  const handleApplyLatest = () => {
    const changed =
      localType.value !== appliedType.value ||
      Number(localLimit) !== Number(appliedLimit);

    if (!changed) return; // <- no query if no change

    const nextLimit = Math.max(1, Number(localLimit) || 1);
    pendingApplyRef.current = { nextType: localType, nextLimit };
    setIsApplying(true);
  };

  const hideList = isApplying || loading_latest_pgrecipes;

  const truncate = (text, max = 100) =>
    !text ? 'N/A' : text.length > max ? text.slice(0, max) + '…' : text;

  return (
    <>
      {/* Header */}
      <div className="mb-4">
        <h6>
          <span style={{ color: themeColor }}>PAGES</span> / LATEST RECIPES
        </h6>
        <h1 className="page-header mb-0">LATEST RECIPES</h1>
      </div>

      {/* Crafted Cocktails (reusable component) */}
      <Card>
        <CardBody>
          <CraftedCocktailsExpandCard />
        </CardBody>
      </Card>

      {/* Latest Recipes list (apply-on-click) */}
      <Card style={{ marginTop: '20px' }}>
        <h4 style={{ marginTop: '20px', marginLeft: '20px' }}>LATEST RECIPES</h4>
        <CardBody>
          {/* Filters */}
          <div className="d-flex justify-content-center align-items-end flex-wrap gap-3 mb-3">
            <div style={{ minWidth: 260 }}>
              <label>Type</label>
              <Select
                options={typeOptions}
                value={localType}
                onChange={setLocalType}
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
                styles={{ menuPortal: base => ({ ...base, zIndex: 11000 }) }}
              />
            </div>

            <div style={{ minWidth: 180 }}>
              <label>Limit</label>
              <Form.Control
                type="number"
                min={1}
                value={localLimit}
                onChange={e => setLocalLimit(e.target.value)}
              />
            </div>

            <div className="d-flex align-items-end">
              <button className="btn btn-secondary" onClick={handleApplyLatest} type="button">
                <i className="fas fa-search" />
              </button>
            </div>
          </div>

          {/* Summary */}
          <div className="d-flex justify-content-end mb-2 fs-6">
            {loading_latest_pgrecipes ? (
              <small>Loading...</small>
            ) : (
              <small>
                <span className="text-inverse">Showing </span>
                <span style={{ color: themeColor }}>{latest_pgrecipes_info.length}</span>
                <span className="text-inverse"> recipes</span>
              </small>
            )}
          </div>

          {/* List with spinner overlay; keep content mounted & hidden to avoid wiggle */}
          <div className="position-relative" style={{ minHeight: 220 }}>
            <div style={{ visibility: hideList ? 'hidden' : 'visible' }}>
              {latest_pgrecipes_error ? (
                <div className="text-danger">{latest_pgrecipes_error}</div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {latest_pgrecipes_info.map((recipe, idx) => (
                    <div
                      key={`${recipe.type}-${recipe.id || idx}`}
                      className="d-flex flex-wrap p-3 product_border"
                    >
                      {/* Left: Rank, Image & Details */}
                      <div
                        className="d-flex align-items-center flex-wrap"
                        style={{ flex: '0 1 60%', minWidth: 0 }}
                      >
                        <div
                          className="d-flex align-items-center me-4 mb-2 mb-md-0"
                          style={{ flexShrink: 0 }}
                        >
                          <div
                            className="text-center fw-bold me-2"
                            style={{ width: '2.5rem', color: themeColor }}
                          >
                            {idx + 1}
                          </div>
                          <img
                            src={recipe.image ? recipe.image : glassshimmer}
                            onError={e => {
                              e.currentTarget.src = glassshimmer;
                            }}
                            alt={recipe.name}
                            className="img-fluid"
                            style={{
                              width: '5rem',
                              height: '5rem',
                              objectFit: 'cover',
                              borderRadius: '0.25rem',
                              backgroundColor: '#fff',
                            }}
                          />
                        </div>
                          <div className="flex-grow-1 mb-2 mb-md-0" style={{ minWidth: 0 }}>
                            <h5 className="mb-1 fs-6">
                              {recipe.name}{' '}
                              <span className="text-inverse fs-7">
                                ({recipe.type?.charAt(0).toUpperCase() + recipe.type?.slice(1)})
                              </span>
                            </h5>
                            <div className="text-inverse fs-7">
                              Device: {recipe.deviceId || 'N/A'}
                            </div>
                            <div className="text-inverse fs-7 mt-1">
                              Created On: {new Date(recipe.created).toLocaleString()}
                            </div>
                          </div>

                      </div>

                      {/* Right: Ingredients */}
                      <div
                        className="d-grid"
                        style={{
                          flex: '0 1 40%',
                          gridTemplateColumns: '5rem 1fr',
                          columnGap: '0.5rem',
                          rowGap: '0.25rem',
                          minWidth: 0,
                        }}
                      >
                        <span className="fw-semibold fs-6 text-nowrap" style={{ color: themeColor }}>
                          Base:
                        </span>
                        <span title={recipe.baseList} className="text-truncate">
                          {truncate(recipe.baseList, 100)}
                        </span>

                        <span
                          className="fw-semibold fs-6 text-nowrap"
                          style={{ color: themeColor }}
                        >
                          Mixer:
                        </span>

                        <span title={recipe.mixerList} className="text-truncate">
                          {truncate(recipe.mixerList, 150)}
                        </span>

                        {recipe.type !== 'unknown' && (
                          <>
                            <span
                              className="fw-semibold fs-6 text-nowrap"
                              style={{ color: themeColor }}
                            >
                              Garnish:
                            </span>
                            <span title={recipe.garnishList} className="text-truncate">
                              {truncate(recipe.garnishList, 150)}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {hideList && (
              <div className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center">
                <div className="spinner-border" style={{ color: themeColor }} />
              </div>
            )}
          </div>
        </CardBody>
      </Card>
    </>
  );
};

export default LatestRecipesPG;
